import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles, TrendingUp, BookOpen, Heart, LogOut, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Welcome = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error) throw error;
      
      setProfile(data);
    } catch (error) {
      console.error("Error loading profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Logged out",
        description: "You've been successfully logged out.",
      });
      
      navigate("/");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getPersonalizedMessage = () => {
    if (!profile) return "Welcome to your wellness journey!";
    
    const stage = profile.journey_stage;
    if (stage === "perimenopause") {
      return "We're here to support you through perimenopause with personalized insights and tools.";
    } else if (stage === "menopause") {
      return "Your menopause journey is unique, and we're here to help you thrive.";
    } else if (stage === "postmenopause") {
      return "Welcome to your postmenopause wellness journey - let's maintain your vitality!";
    }
    return "We're excited to support your wellness journey!";
  };

  const getGoalDescription = (goal: string) => {
    switch (goal) {
      case "understand":
        return "Gain clarity and understanding about what's happening to your body";
      case "manage":
        return "Develop practical strategies to manage and relieve your symptoms";
      case "connect":
        return "Find community support and connect with others on similar journeys";
      case "optimize":
        return "Optimize your health and thrive through this life transition";
      default:
        return goal.replace(/_/g, " ");
    }
  };

  const getSymptomDescription = (symptom: string) => {
    switch (symptom) {
      case "metabolic":
        return "Managing metabolic changes - weight, hot flashes, and energy levels";
      case "neuro":
        return "Supporting cognitive and emotional wellness - brain fog, mood, anxiety, sleep";
      case "urogenital":
        return "Addressing intimate health - vaginal health, bladder changes, libido";
      case "physical":
        return "Maintaining physical vitality - joint health, muscle strength, skin care";
      default:
        return symptom.replace(/_/g, " ");
    }
  };

  const getJourneyStageDescription = () => {
    if (!profile) return "";
    
    switch (profile.journey_stage) {
      case "perimenopause":
        return "You're navigating perimenopause - a time of transition and change";
      case "menopause":
        return "You're in menopause - adapting to your body's natural evolution";
      case "postmenopause":
        return "You're in postmenopause - focusing on long-term wellness";
      case "unsure":
        return "We'll help you understand where you are in your journey";
      default:
        return "";
    }
  };

  const handleContinue = async () => {
    try {
      // Mark onboarding as completed so this welcome screen won't show again
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("user_id", user.id);

      if (error) throw error;

      navigate("/");
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 p-4 flex items-center justify-center">
      <div className="max-w-2xl w-full space-y-6">
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="gap-2"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
        <Card className="text-center">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-3xl">Welcome to Your Journey!</CardTitle>
            <CardDescription className="text-lg mt-2">
              {getPersonalizedMessage()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Disclaimer */}
            <div className="p-6 bg-amber-50 dark:bg-amber-950/20 border-2 border-amber-200 dark:border-amber-800 rounded-lg text-left">
              <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-3 flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-amber-700 dark:text-amber-300" />
                Important Disclaimer
              </h3>
              <div className="space-y-2 text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium">This app is designed to:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Help you track and understand your symptoms</li>
                  <li>Provide educational content about menopause</li>
                  <li>Support your wellness journey with tools and resources</li>
                </ul>
                
                <p className="font-medium mt-4">This app is NOT:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>A substitute for professional medical advice</li>
                  <li>A diagnostic tool for medical conditions</li>
                  <li>A replacement for consultations with healthcare providers</li>
                </ul>
                
                <p className="font-semibold mt-4 text-amber-900 dark:text-amber-100">
                  Always consult with your healthcare provider about your symptoms and treatment options.
                </p>
              </div>
            </div>

            {/* Personalized Profile Summary */}
            {profile && (
              <div className="p-6 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg text-left space-y-4">
                <h3 className="font-semibold text-lg mb-3">Your Personalized Journey</h3>
                
                {profile.journey_stage && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Where You Are:</p>
                    <p className="text-foreground">{getJourneyStageDescription()}</p>
                  </div>
                )}

                {profile.symptoms && profile.symptoms.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Your Focus Area:</p>
                    {profile.symptoms.map((symptom: string, index: number) => (
                      <p key={index} className="text-foreground">
                        {getSymptomDescription(symptom)}
                      </p>
                    ))}
                  </div>
                )}
                
                {profile.goals && profile.goals.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">What We'll Help You With:</p>
                    <ul className="space-y-2">
                      {profile.goals.map((goal: string, index: number) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-primary mt-1">✓</span>
                          <span className="text-foreground">{getGoalDescription(goal)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {profile.knowledge_level && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Your Knowledge Level:</p>
                    <p className="text-foreground capitalize">{profile.knowledge_level.replace(/_/g, " ")}</p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-secondary/30 rounded-lg">
                <TrendingUp className="h-6 w-6 text-primary mx-auto mb-2" />
                <h3 className="font-semibold mb-1">Track Progress</h3>
                <p className="text-sm text-muted-foreground">
                  Monitor symptoms and celebrate improvements
                </p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <BookOpen className="h-6 w-6 text-primary mx-auto mb-2" />
                <h3 className="font-semibold mb-1">Learn & Grow</h3>
                <p className="text-sm text-muted-foreground">
                  Access personalized educational content
                </p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <Heart className="h-6 w-6 text-primary mx-auto mb-2" />
                <h3 className="font-semibold mb-1">Self-Care Tools</h3>
                <p className="text-sm text-muted-foreground">
                  Emergency support when you need it most
                </p>
              </div>
            </div>

            <Button
              size="lg"
              className="w-full"
              onClick={handleContinue}
            >
              I Understand - Let's Begin!
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Welcome;
